import { useGetPagedBroadcastByFilter } from "hooks/useGetPagedBroadcastByFilter";
import { usePageHeader } from "hooks/usePageHeader";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { VideoLibraryBroadcastList } from "../VideoLibraryPage/VideoLibraryBroadcastList";
import { SearchSortOptions } from "components/generic-multiselect/types";
import { SortOptions } from "components/inputs/search-sort-bar/types";
import { useMemo } from "react";
import { Link } from "components/buttons/Link";
import { Button } from "components/buttons/Button";
import styles from "./index.module.scss";
import { useDispatch } from "react-redux";
import { resetAllForms } from "store/platforms/slice";
import { useGetCloudUsageForUploadSessions } from "components/video-upload/hooks/useGetCloudUsageForUploadSessions";
import { useSwitcherSdk } from "hooks/useSwitcherSdk";
import { Modals } from "store/modal/types";
import { setActiveModal } from "store/modal/slice";
import { NewLivestreamModal } from "components/modal/NewLivestreamModal";

const newLivestreamCreationEnabled =
    import.meta.env.VITE_NEW_LIVESTREAM_CREATION_ENABLED === "true";

export const LivestreamsPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { libraryFull } = useGetCloudUsageForUploadSessions();
    const { isInBrowser } = useSwitcherSdk();

    usePageHeader({
        subTitle: (
            <>
                <p>
                    {t("livestreams-page:subtitle-line1")}{" "}
                    <Link
                        to="https://itunes.apple.com/us/app/switcher-studio-pro/id908386221"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t("livestreams-page:subtitle-link")}
                    </Link>
                    <br />
                    {t("livestreams-page:subtitle-line2")}
                </p>
            </>
        )
    });

    const {
        refetchVideos,
        firstLoading,
        upcomingLivestreams,
        fetchBroadcastsWithNewParams,
        getNextPage,
        nextPageLoading,
        refetchFromPageOne,
        allLoaded
    } = useGetPagedBroadcastByFilter({
        videoStatusType: "Livestream",
        itemsPerPage: 15
    });

    const searchSortOptions = useMemo<SearchSortOptions>(
        () => ({
            showSearchSort: true,
            defaultSortOption: SortOptions.Next,
            hiddenSortOptions: [
                SortOptions.NewestFirst,
                SortOptions.OldestFirst,
                SortOptions.LeastViews,
                SortOptions.MostViews,
                SortOptions.TitleAZ,
                SortOptions.TitleZA
            ]
        }),
        []
    );

    const handleStartNewStream = () => {
        // Reset all forms before navigating
        dispatch(resetAllForms());
        if (isInBrowser && newLivestreamCreationEnabled) {
            dispatch(
                setActiveModal({
                    id: "new-livestream",
                    type: Modals.NewLivestreamModal,
                    component: <NewLivestreamModal />
                })
            );
        } else {
            navigate("/platforms/simulcast/destinations");
        }
    };

    return (
        <>
            {libraryFull && (
                <div className={styles["new-stream-button-warning"]}>
                    <Trans
                        i18nKey={`platforms:storage-limit-reached-schedule-new`}
                        components={{
                            link1: <Link to="/subscription#openModal" />
                        }}
                    />
                </div>
            )}
            <div className={styles["new-stream-button-container"]}>
                <Button type="primary" onClick={handleStartNewStream}>
                    {t("platforms:schedule-new")}
                </Button>
            </div>
            <VideoLibraryBroadcastList
                broadcasts={upcomingLivestreams}
                refetchVideos={refetchVideos}
                firstLoading={firstLoading}
                searchSortOptions={{
                    ...searchSortOptions,
                    refetchWithNewParams: (params) =>
                        fetchBroadcastsWithNewParams(params)
                }}
                emptyStateCopy={t("livestreams-page:no-livestreams")}
                onEdit={(broadcast) =>
                    navigate(
                        `/scheduled-livestreams/broadcast/${broadcast.Details.Id}`
                    )
                }
                getNextPage={getNextPage}
                nextPageLoading={nextPageLoading}
                refetchFromPageOne={refetchFromPageOne}
                allLoaded={allLoaded}
            />
        </>
    );
};
